import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "oss-admin-token";
const SAVED_MSG_MS = 3000;
const HTTP_UNAUTHORIZED = 401;

export default function Admin() {
  // sessionStorage instead of localStorage — token clears on browser close, smaller XSS blast radius.
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("admin@ossottawa.com");
  const [password, setPassword] = useState("");
  const [settings, setSettings] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/settings`);
    const data = r.data || {};
    if (!data.category_images) data.category_images = {};
    setSettings(data);
  }, []);
  useEffect(() => { if (token) load(); }, [token, load]);

  const login = async () => {
    setErr("");
    try {
      const r = await axios.post(`${API}/admin/login`, { email, password });
      sessionStorage.setItem(TOKEN_KEY, r.data.token);
      setToken(r.data.token);
    } catch { setErr("Invalid email or password"); }
  };

  const logout = () => { sessionStorage.removeItem(TOKEN_KEY); setToken(""); setSettings(null); };

  const save = async () => {
    setErr(""); setMsg("");
    try {
      await axios.put(`${API}/settings`, settings, { headers: { Authorization: `Bearer ${token}` } });
      setMsg("Saved · live now");
      setTimeout(() => setMsg(""), SAVED_MSG_MS);
    } catch (e) {
      if (e?.response?.status === HTTP_UNAUTHORIZED) { logout(); setErr("Session expired"); }
      else setErr("Save failed");
    }
  };

  const resetToDefaults = async () => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm("Reset menu to the latest code defaults? This overwrites every category blurb, item list, and deal with the new defaults. Uploaded images are preserved.")) return;
    setErr(""); setMsg("");
    try {
      await axios.post(`${API}/admin/reset-settings`, {}, { headers: { Authorization: `Bearer ${token}` } });
      await load();
      setMsg("Reset to defaults · live now");
      setTimeout(() => setMsg(""), SAVED_MSG_MS);
    } catch (e) {
      if (e?.response?.status === HTTP_UNAUTHORIZED) { logout(); setErr("Session expired"); }
      else setErr("Reset failed");
    }
  };

  const upd = (path, v) => setSettings((s) => { const c = structuredClone(s); let o = c; for (let i=0;i<path.length-1;i++) o = o[path[i]]; o[path[path.length-1]] = v; return c; });

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black p-6">
        <div className="w-full max-w-sm rounded-3xl border border-[#00ff41]/25 bg-[#0a0a0a] p-8">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00ff41] mb-2">OSS Admin</p>
          <h1 className="font-display text-3xl font-extrabold mb-6">Sign in</h1>
          <input data-testid="admin-email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="email" className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 mb-3 outline-none focus:border-[#00ff41]" />
          <input data-testid="admin-password" value={password} onChange={(e)=>setPassword(e.target.value)} onKeyDown={(e)=>e.key==='Enter'&&login()} type="password" placeholder="password" className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 mb-3 outline-none focus:border-[#00ff41]" />
          {err && <p className="text-sm text-red-400 mb-3">{err}</p>}
          <button data-testid="admin-login-btn" onClick={login} className="w-full rounded-full bg-[#00ff41] text-black font-display font-bold py-3 hover:bg-[#00e63a]">Sign in</button>
        </div>
      </div>
    );
  }

  if (!settings) return <div className="min-h-screen flex items-center justify-center bg-black text-white">Loading…</div>;

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <div className="sticky top-0 z-10 border-b border-white/10 bg-black/85 backdrop-blur px-5 sm:px-8 py-4 flex items-center gap-3">
        <p className="font-display text-lg font-extrabold mr-auto">OSS Admin</p>
        {msg && <span className="text-sm text-[#00ff41]">{msg}</span>}
        {err && <span className="text-sm text-red-400">{err}</span>}
        <button data-testid="admin-reset-btn" onClick={resetToDefaults} className="rounded-full border border-orange-400/40 bg-orange-400/10 px-4 py-2 text-sm font-display font-semibold text-orange-300 hover:bg-orange-400/20">Reset to defaults</button>
        <button data-testid="admin-save-btn" onClick={save} className="rounded-full bg-[#00ff41] text-black font-display font-bold px-5 py-2 hover:bg-[#00e63a]">Save</button>
        <button onClick={logout} className="rounded-full border border-white/15 px-4 py-2 text-sm">Logout</button>
      </div>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8 space-y-10">
        {/* Shop */}
        <Section title="Shop">
          <Row label="Phone (E.164)"><Input data-testid="set-phone" value={settings.phone} onChange={(v)=>upd(["phone"],v)} /></Row>
          <Row label="Phone display"><Input data-testid="set-phone-display" value={settings.phoneDisplay} onChange={(v)=>upd(["phoneDisplay"],v)} /></Row>
          <Row label="Free delivery threshold ($)"><Input type="number" value={settings.freeDeliveryThreshold} onChange={(v)=>upd(["freeDeliveryThreshold"], Number(v)||0)} /></Row>
        </Section>

        {/* Categories */}
        <Section title="Categories">
          {Object.entries(settings.categories).map(([id, cat]) => (
            <div key={id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="font-mono text-xs uppercase tracking-widest text-[#00ff41]">{id}</p>
                <ImageUploader categoryId={id} token={token} currentPath={settings.category_images?.[id]} onUploaded={(p)=>upd(["category_images",id], p)} />
              </div>
              <Row label="Blurb"><Input value={cat.blurb} onChange={(v)=>upd(["categories",id,"blurb"],v)} /></Row>
              <Row label="Items (one per line)">
                <textarea
                  data-testid={`set-items-${id}`}
                  value={(cat.items || []).join("\n")}
                  onChange={(e)=>upd(["categories",id,"items"], e.target.value.split("\n").map((x)=>x.trim()).filter(Boolean))}
                  rows={Math.max(3, (cat.items||[]).length + 1)}
                  className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 outline-none focus:border-[#00ff41] font-mono text-sm"
                />
              </Row>
            </div>
          ))}
        </Section>

        {/* Deals */}
        <Section title="Daily deals">
          {settings.deals.map((d, i) => (
            <div key={d.id || i} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <p className="font-mono text-xs uppercase tracking-widest text-[#00ff41] mb-2">{d.id}</p>
              <Row label="Tag EN"><Input value={d.tag_en} onChange={(v)=>upd(["deals",i,"tag_en"],v)} /></Row>
              <Row label="Tag FR"><Input value={d.tag_fr} onChange={(v)=>upd(["deals",i,"tag_fr"],v)} /></Row>
              <Row label="Title EN"><Input value={d.title_en} onChange={(v)=>upd(["deals",i,"title_en"],v)} /></Row>
              <Row label="Title FR"><Input value={d.title_fr} onChange={(v)=>upd(["deals",i,"title_fr"],v)} /></Row>
              <Row label="Body EN"><Input value={d.body_en} onChange={(v)=>upd(["deals",i,"body_en"],v)} /></Row>
              <Row label="Body FR"><Input value={d.body_fr} onChange={(v)=>upd(["deals",i,"body_fr"],v)} /></Row>
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <h2 className="font-display text-2xl font-extrabold mb-4">{title}</h2>
    <div className="space-y-4">{children}</div>
  </div>
);
const Row = ({ label, children }) => (
  <label className="block mb-3">
    <span className="block text-xs text-neutral-400 mb-1 font-mono uppercase tracking-widest">{label}</span>
    {children}
  </label>
);
const Input = ({ value, onChange, type = "text", ...rest }) => (
  <input type={type} value={value ?? ""} onChange={(e)=>onChange(e.target.value)} className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 outline-none focus:border-[#00ff41]" {...rest} />
);

const ImageUploader = ({ categoryId, token, currentPath, onUploaded }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(`${apiBase}/admin/upload-category-image?category_id=${encodeURIComponent(categoryId)}`, fd, { headers: { Authorization: `Bearer ${token}` } });
      onUploaded(r.data.path);
    } catch (ex) {
      setErr("Upload failed");
    } finally { setBusy(false); e.target.value = ""; }
  };
  let uploadLabel;
  if (busy) uploadLabel = "Uploading…";
  else if (currentPath) uploadLabel = "Replace";
  else uploadLabel = "Upload image";
  return (
    <div className="flex items-center gap-2">
      {currentPath ? (
        <img src={`${apiBase}/files/${currentPath}`} alt="" className="h-10 w-10 rounded-lg object-cover border border-white/10" />
      ) : null}
      <label className={`cursor-pointer rounded-full border border-[#00ff41]/40 bg-[#00ff41]/10 px-3 py-1 text-xs font-display font-bold text-[#00ff41] hover:bg-[#00ff41]/20 ${busy ? "opacity-50 pointer-events-none" : ""}`}>
        {uploadLabel}
        <input data-testid={`img-upload-${categoryId}`} type="file" accept="image/*" className="hidden" onChange={onPick} />
      </label>
      {err && <span className="text-xs text-red-400">{err}</span>}
    </div>
  );
};
